/* eslint-disable eqeqeq */
/**
 * COMBO LOUVER (KA-5245) — sub-phases A1.2 + A1.3 + A1.4 + A1.6: THE ENGINE.
 *
 * Everything stateful/asynchronous the combo dialog does: the `aw_dims` option
 * fetcher, the per-face `master_louver_id` lookups, the resize/preview
 * round-trips through `PATCH /api/v1.0/ahu/louvers/<id>/with_position` — and
 * (wave 3) the face-row handlers, Reset, the `.not-valid` pass and the PERSIST
 * (`PATCH /api/v1.0/ahu/louvers/<id>`, the one call that actually saves).
 *
 * ⚠ WHY THIS IS A NEW MODULE AND NOT `useAutoSize.ts`. The inventory (§10) is
 * explicit: `useAutoSize` is SYNC, PURE, SINGLE-dimension-pair, returns
 * `boolean`, and snaps against `cmsAccessory[widthField]` arrays. It structurally
 * cannot express "await the resize, THEN refetch the option lists". The combo
 * sizing formula itself lives SERVER-side (`engineering/combo_louver.py`), reached
 * only through `/with_position`; the client's whole job is ordering.
 *
 * ★ THE ONE RULE THIS FILE EXISTS TO ENFORCE (inventory §5.1). The aw_dims query
 * carries the CURRENT `enclosure_depth` / `enclosure_width` / `enclosure_height`
 * and `auto_size`, and those params BOUND the returned option lists. So any action
 * that resizes the enclosure invalidates the lists, and the refetch must happen
 * AFTER the resize promise resolves. Two dev commits encode that and are built in
 * here from the start:
 *   · 3135165b0d — `toggleComboAutoSize` (:1817-1823) does
 *     `_maybeComboAutoSize().then(() => _fetchComboAwDims())`; `maybeAutoSize()`
 *     below therefore RETURNS A PROMISE that resolves only after the
 *     `/with_position` round trip completes (:2563-2581).
 *   · a092d3353c + 56ff2152ec — the `casing_mount_id` branch (:2408-2420) calls
 *     BOTH `_fetchComboAwDims()` AND `_maybeComboAutoSize()`/`_comboRecalc()`.
 *     Without the `_comboRecalc()` half the enclosure never resizes AT ALL while
 *     auto-size is off. (That branch lives in AccessorySelector's `onModelField`.)
 *
 * ★ AND THE THREE RESIDUAL ORDERING DEFECTS the two commits did NOT cover are
 * MIRRORED, not fixed — parity-only, legacy bugs are requirements:
 *   · Q2 — `applySmartlistPick` fetches aw_dims BEFORE the resize (see it there);
 *   · Q3 — the `casing_mount_id` path likewise (AccessorySelector `onModelField`);
 *   · Q4/R4 — the enclosure GROWS BUT NEVER SHRINKS. Server-side and therefore
 *     inherited: `combo_fit_enclosure_to_faces` is `max()`-only
 *     (`models_accessory_louver.py:496-498`). The client must NOT compensate —
 *     see `applyEnclosureEcho` below, which echoes whatever the server returns.
 *
 * ⚠ `/with_position` NEVER PERSISTS (inventory §4 W2). It is the live preview.
 * Three server modes, dispatched purely by the BODY (`louver_viewset.py:137-149`):
 *   1. `desired_face_velocity` truthy → `combo_autosize(...)` → the FULL
 *      sides-first resize (sides are capped by the depth so they FILL FIRST; the
 *      front pays the remainder). Sent by `autoSize()` and by `getAccessoryData`
 *      whenever `shouldAutoSize()`.
 *   2. `resize_sides_from_depth` truthy → DEAD-8, zero legacy senders. NOT PORTED.
 *   3. neither → `combo_fit_enclosure_to_faces(edited_face=…)` → the GROW-ONLY
 *      fit, where only `edited_face` may grow a dimension. Sent by `recalc()` and
 *      by `getAccessoryData` when auto-size is off and the combo is already sized.
 *
 * ⚠ RISK R7: every `combo_*` CMS limit is NULL on dev3, so no clamp branch here
 * or server-side can be exercised on that DB. They are implemented from the code.
 */

import type { StagingModel } from './accessorySelectorStaging';
import type { WithPositionBody, WithPositionResponse } from './accessorySelectorApi';
import type { ComboAwDimsResponse, ComboMasterLouverRow } from './comboLouverApi';
import { parseParams } from './accessorySelectorData';
import {
  COMBO_FACES,
  buildComboAwDimsUrl,
  buildComboMasterLouverUrl,
  comboActualFaceVelocity,
  comboAnyFaceChecked,
  comboFaceLouverParamsComplete,
  comboIdentityComplete,
  comboSharedIdentity,
  comboSharedIdentityActive,
  emptyComboAwDims,
  resetComboFaceAwSelections,
  resetComboFaces,
  resetComboIdentityParams,
  snapshotComboMissingOptions,
  applyComboSmartlistPickParams,
  type ComboAwDims,
  type ComboFace,
  type ComboFacesState,
  type ComboFaceState,
  type ComboSmartlistRow,
} from './comboLouver';

/** `(v === '' || v == null) ? null : parseFloat(String(v).replace(/,/g,''))` —
 * the `num` closure legacy re-declares in five places (:1248, :1376, :1590,
 * :2671, :3668). Commas because `validateNumberField` writes `2,430.00`. */
export const comboNum = (v: unknown): number | null =>
  v === '' || v == null ? null : parseFloat(String(v).replace(/,/g, ''));

/** One face as `/with_position` wants it. Key ORDER is legacy's verbatim so the
 * wire body diffs byte-identically (§4 W2). */
interface ComboFaceBody {
  face: ComboFace;
  checked: boolean;
  master_louver_id: number | null;
  aw_width?: number | null;
  aw_height?: number | null;
}

export interface ComboEngineCtx {
  /** `self.mfg.id` — 1 on dev3, matching every other React CMS URL. */
  cmsMfgId: number;
  /** `self.section.get('id')` — every `/with_position` body carries it. */
  sectionId: number;

  comboMode: () => boolean;
  /** `self._comboFaces` — MUTATED in place, exactly as legacy does. */
  faces: () => ComboFacesState | null;
  awDims: () => ComboAwDims;
  setAwDims: (dims: ComboAwDims) => void;
  autoSizeEnabled: () => boolean;
  staging: () => StagingModel | null;
  stagingPosition: () => StagingModel | null;
  cms: () => Record<string, unknown> | null;

  /** raw-URL GETs. Both REJECT on a non-2xx — see `fetchAwDims`/Q5. */
  getAwDims: (url: string) => Promise<ComboAwDimsResponse>;
  getMasterLouvers: (url: string) => Promise<ComboMasterLouverRow[]>;
  /** PATCH /ahu/louvers/<id>/with_position. Resolves null on failure. */
  patchWithPosition: (body: WithPositionBody) => Promise<WithPositionResponse | null>;
  /** A1.6 — PATCH /ahu/louvers/<id> (NO trailing slash — `/louvers/5/` 404s,
   * urls.py `r'^louvers/(?P<pk>[0-9]+)$'`). THE persist. Resolves null on
   * failure, matching legacy's `fn.uni_tools.patch(..., {retries:1})` whose
   * throw lands in a bare `if (data)`. */
  patchLouver: (body: Record<string, unknown>) => Promise<Record<string, unknown> | null>;
  /** `self.section.get('aw_height')` — `_applyComboValidation` :1060 caps the
   * enclosure height at the section airway height on top of the CMS range. */
  sectionAwHeight: () => unknown;

  /** `self.render()`. */
  render: () => void;
  updateCanvasPreview: () => void;
  /** `self.getAccessoryData()` — the general round-trip. */
  getAccessoryData: () => Promise<void>;
  /** `self._updateMountingImage(params)`. */
  setImgComponent: () => void;
  /** arm the userArmed persist gate (legacy's non-silent `staging_model.set`). */
  markChanged: () => void;
  /** `self._paramsChangedAutoPick = true`. */
  setParamsChangedAutoPick: () => void;
  /** `closeComboSmartlist()` :1560-1570 — flips the popup back to `data-tab="ahu"`. */
  closeSmartlist: () => void;
  /** the raw string in a dialog `input[name=…]`, or null when absent. Legacy
   * reads several values straight off the DOM rather than off the staging model
   * (`_comboReady` :2534-2535, `_fetchComboAwDims` :1377-1382, `_comboAutoSize`
   * :2591-2592) — reproduced so the "DOM transiently incomplete during a render
   * burst" behaviour the debounce exists for is preserved. */
  domValue: (name: string) => string | null;
}

export interface ComboEngine {
  fetchAwDims: () => Promise<void>;
  recalc: (opts?: { face?: ComboFace | null }) => void;
  maybeAutoSize: () => Promise<void>;
  autoSize: () => Promise<void>;
  neverSized: () => boolean;
  shouldAutoSize: () => boolean;
  ready: () => boolean;
  recalcMasterLouverId: (face: ComboFace) => Promise<void> | undefined;
  recalcAllMasterLouverIds: () => void;
  /** `getAccessoryData`'s per-face echo (:3762-3772) — ALL faces, no `edited_face`
   * filter. Exposed because that round trip lives in AccessorySelector. */
  applyServerFaces: (rows: Array<Record<string, unknown>>) => void;
  awaitPendingLookups: () => Promise<void>;
  applySmartlistPick: (row: ComboSmartlistRow) => Promise<void>;
  updateFaceVelocity: () => void;
  updateFaceVelocityColor: () => void;
  faceBodies: (withDims: boolean) => ComboFaceBody[];
  positionBody: () => Record<string, unknown>;
  /** A1.4 — `toggleComboFace` :1832-1872. */
  toggleFace: (face: ComboFace, checked: boolean) => void;
  /** A1.4 — `onComboFaceFieldChange` :1131-1159. */
  onFaceField: (face: ComboFace, name: 'combo_aw_width' | 'combo_aw_height', raw: string) => void;
  /** A1.4 — `resetComboFace` :1512-1541. */
  reset: () => void;
  /** A1.4 — `_applyComboValidation` :1038-1087 (the `.not-valid` DOM pass). */
  applyValidation: () => void;
  /** A1.6 — `_persistCombo` :2667-2714. Resolves the server row, or null. */
  persist: () => Promise<Record<string, unknown> | null>;
  dispose: () => void;
}

const BLANK_FACE = (): ComboFaceState => ({ checked: true, aw_width: '', aw_height: '' });

export function createComboEngine(ctx: ComboEngineCtx): ComboEngine {
  /** `self._comboAwDimsReqId` :1363 — drops a stale aw_dims response when the
   * params changed again before it resolved. */
  let awDimsReqId = 0;
  /** ⚠ QUIRK Q11: `_comboRecalcTimer` and `_comboAutoTimer` are SEPARATE timers
   * (:1244 vs :2571), so a burst can fire BOTH a grow-only recalc and a full
   * auto-size. Kept separate deliberately. */
  let recalcTimer: ReturnType<typeof setTimeout> | null = null;
  let autoTimer: ReturnType<typeof setTimeout> | null = null;
  /** legacy `_comboFaces[face]._pendingLookup` (:1778) — the in-flight per-face
   * `/louvers/all/` settle promise `awaitPendingLookups()` waits on. Held here
   * rather than on the face state so a promise never leaks into render props. */
  const pendingLookups: Record<string, Promise<void> | null> = {
    front: null,
    left: null,
    right: null,
  };

  const params = (): Record<string, unknown> =>
    parseParams(ctx.staging()?.get('louver_params'));

  /** legacy's `depthVal ? num(depthVal) : num(model.<k>)` pair (:1377-1382): the
   * DOM input wins when non-empty, else the staging model. */
  const domOrModel = (name: string): number | null => {
    const raw = ctx.domValue(name);
    if (raw) return comboNum(raw);
    return comboNum(ctx.staging()?.get(name));
  };

  const face = (f: ComboFace): ComboFaceState => {
    const bag = ctx.faces();
    if (!bag) return BLANK_FACE();
    if (!bag[f]) bag[f] = BLANK_FACE();
    return bag[f]!;
  };

  // -----------------------------------------------------------------------
  // face velocity (client-side, for immediacy) + the 101 % red rule
  // -----------------------------------------------------------------------

  /**
   * `_updateFaceVelocityColor` :1185-1191 — red once the ACTUAL FV reaches 101 %
   * of the desired one (shared by plain and combo louvers).
   * DEVIATION, deliberate: legacy toggles the class out-of-band with jQuery and
   * NEVER re-applies it from `render()` (the only callers are
   * `_updateComboFaceVelocity`, `_comboRecalc`, `_comboAutoSize` and the
   * `desired_face_velocity_actual` branch of `changeModelField`), so the flag is
   * silently lost on any other re-render. Doing the same DOM toggle here
   * reproduces that exactly; threading it through React state would NOT.
   */
  const updateFaceVelocityColor = (): void => {
    const sm = ctx.staging();
    if (!sm) return;
    const desired = parseFloat(String(sm.get('desired_face_velocity_actual')));
    const actual = parseFloat(String(sm.get('face_velocity_actual')));
    const isOver =
      !Number.isNaN(desired) && desired > 0 && !Number.isNaN(actual) && actual >= desired * 1.01;
    document
      .querySelector<HTMLInputElement>('#component_rating_mode input[name="face_velocity_actual"]')
      ?.classList.toggle('not-valid', isOver);
  };

  /** `_updateComboFaceVelocity` :1174-1180. Legacy writes the input's value
   * directly; React's is controlled, so `render()` is that write. */
  const updateFaceVelocity = (): void => {
    const sm = ctx.staging();
    if (!sm) return;
    sm.set({ face_velocity_actual: comboActualFaceVelocity(ctx.faces(), sm.get('airflow_actual')) });
    ctx.render();
    updateFaceVelocityColor();
  };

  // -----------------------------------------------------------------------
  // A1.2 — per-face master_louver_id lookups (dedup + stale guard + await)
  // -----------------------------------------------------------------------

  /** `_setComboFaceInvalid` :1718-1725 — persisted on the face so it survives
   * `render()`; `ComboFaceView.not_valid` renders it onto both selects. */
  const setFaceInvalid = (f: ComboFace, invalid: boolean): void => {
    face(f).noMatch = !!invalid;
    ctx.render();
  };

  /**
   * `_recalcComboMasterLouverId` :1735-1780. Resolves the catalog louver matching
   * one face's shared identity + its own W×H — the automatic equivalent of picking
   * it from a per-face Smartlist (which does not exist; DEAD-5). Ineligible faces
   * get no lookup and no stale `.not-valid`.
   *  · DEDUP: identical params across faces (both sides usually match) share ONE
   *    request — legacy via `stateStorage.models`, here via RTKQ's URL cache.
   *  · STALE GUARD: `pendingLouverUrl` is the per-face request token; a response
   *    for a URL that is no longer the latest for that face is dropped.
   */
  const recalcMasterLouverId = (f: ComboFace): Promise<void> | undefined => {
    if (!ctx.faces()) return undefined;
    const state = face(f);
    const p = params();
    if (!comboFaceLouverParamsComplete(state, p)) {
      state.master_louver_id = null;
      state.pendingLouverUrl = null;
      pendingLookups[f] = null;
      setFaceInvalid(f, false);
      return undefined;
    }
    const url = buildComboMasterLouverUrl({ cmsMfgId: ctx.cmsMfgId, params: p, face: state });
    state.pendingLouverUrl = url;
    const settle = ctx.getMasterLouvers(url).then(
      (response) => {
        const cur = ctx.faces()?.[f];
        if (!cur || cur.pendingLouverUrl !== url) return;
        const matched = response[0] ?? null;
        if (matched) {
          cur.master_louver_id = matched.id;
          setFaceInvalid(f, false);
        } else {
          cur.master_louver_id = null;
          setFaceInvalid(f, true);
        }
      },
      () => {
        const cur = ctx.faces()?.[f];
        if (!cur || cur.pendingLouverUrl !== url) return;
        cur.master_louver_id = null;
        setFaceInvalid(f, true);
      }
    );
    pendingLookups[f] = settle;
    return settle;
  };

  /** `_recalcAllComboMasterLouverIds` :1786-1792. */
  const recalcAllMasterLouverIds = (): void => {
    if (!ctx.comboMode()) return;
    COMBO_FACES.forEach((f) => recalcMasterLouverId(f));
  };

  /**
   * `_awaitPendingComboLouverLookups` :1799-1805 — ★ RACE GUARD #1 of the two
   * that guarantee a just-resolved `master_louver_id` lands in the SAME round trip
   * instead of racing it. Awaited by `recalc`, `getAccessoryData` and (A1.6)
   * `_persistCombo`.
   */
  const awaitPendingLookups = async (): Promise<void> => {
    const pending = COMBO_FACES.map((f) => pendingLookups[f]).filter(Boolean) as Promise<void>[];
    if (pending.length) await Promise.all(pending);
  };

  // -----------------------------------------------------------------------
  // A1.2 — the aw_dims option lists (TWO PARALLEL calls)
  // -----------------------------------------------------------------------

  /** `_resetComboFaceAwSelections` + `_updateComboAwSelects` + the follow-up
   * lookups — the shared tail of both `fetchAwDims` exits (:1368-1373 / :1406-1410). */
  const reconcileAwSelections = (): void => {
    resetComboFaceAwSelections(
      ctx.faces(),
      ctx.awDims(),
      ctx.autoSizeEnabled(),
      comboSharedIdentityActive(ctx.cms(), params())
    );
    // ★ `_updateComboAwSelects`' restore half, and ONLY here — legacy's append is
    // a one-shot DOM mutation that the next render wipes, so a value the SERVER
    // writes later is never restored. That is Q2 (the side selects stay blank at
    // depth 12 while the faces hold 22 × 62) and it must be mirrored.
    snapshotComboMissingOptions(ctx.faces(), ctx.awDims());
    updateFaceVelocity(); // :1466 — the tail of _resetComboFaceAwSelections
    ctx.render(); // = _updateComboAwSelects (React's selects are declarative)
    recalcAllMasterLouverIds();
  };

  /**
   * `_fetchComboAwDims` :1354-1411 — TWO PARALLEL GETs off one base URL
   * (`&front=true` + `&side=true`), because the front width is bounded by the CMS
   * max (or, with `auto_size=false`, by the request's own `&width=`) and each side
   * width by the box DEPTH. Left/right share the `side` list — both sides are
   * symmetric in the enclosure.
   *
   * ⚠ REJECTS, deliberately, and the rejection is NOT swallowed here (QUIRK Q5 /
   * RISK R1). After Reset the query carries `&width=null&height=null` and
   * `louvers_viewset.py:692-715` 500s on the bare `float()`. In legacy that
   * rejection escapes `Promise.all`, so `_comboAwDims` keeps its previous value
   * and the three follow-ups never run — and where a caller AWAITS this
   * (`_applyComboSmartlistPick` :1621) the REST OF THAT CHAIN is abandoned too,
   * which is exactly why the measured outcome is "after Reset the combo cannot be
   * re-sized until the depth is edited". Catching it here would DIVERGE.
   */
  const fetchAwDims = async (): Promise<void> => {
    if (!ctx.comboMode()) return;
    const identity = comboSharedIdentity(params());
    const reqId = ++awDimsReqId;

    if (!comboIdentityComplete(identity)) {
      // identity not fully picked → no face can be eligible; lists go empty.
      ctx.setAwDims(emptyComboAwDims());
      reconcileAwSelections();
      return;
    }

    const base = buildComboAwDimsUrl({
      cmsMfgId: ctx.cmsMfgId,
      identity,
      depth: domOrModel('enclosure_depth'),
      autoSize: ctx.autoSizeEnabled(),
      width: domOrModel('enclosure_width'),
      height: domOrModel('enclosure_height'),
    });
    const [frontData, sideData] = await Promise.all([
      ctx.getAwDims(`${base}&front=true`),
      ctx.getAwDims(`${base}&side=true`),
    ]);
    if (reqId !== awDimsReqId) return; // superseded by a newer params change

    ctx.setAwDims({
      front: { aw_widths: frontData?.aw_widths ?? [], aw_heights: frontData?.aw_heights ?? [] },
      side: { aw_widths: sideData?.aw_widths ?? [], aw_heights: sideData?.aw_heights ?? [] },
    });
    reconcileAwSelections();
  };

  // -----------------------------------------------------------------------
  // A1.3 — the /with_position bodies
  // -----------------------------------------------------------------------

  /** the per-face payload. `withDims` false = `_comboAutoSize`'s shape
   * (`{face, checked, master_louver_id}` only — the server derives the sizes);
   * true = `_comboRecalc` / `getAccessoryData` / (A1.6) `_persistCombo`.
   * `active` is NEVER sent — it is derived server-side (`louver_viewset.py:135`). */
  const faceBodies = (withDims: boolean): ComboFaceBody[] =>
    COMBO_FACES.map((f) => {
      const s = ctx.faces()?.[f] ?? ({} as ComboFaceState);
      const body: ComboFaceBody = {
        face: f,
        checked: s.checked !== false,
        master_louver_id: s.master_louver_id || null,
      };
      if (withDims) {
        body.aw_width = comboNum(s.aw_width);
        body.aw_height = comboNum(s.aw_height);
      }
      return body;
    });

  /**
   * `_comboPositionBody` :1222-1232 — the LIVE staging position, not `{}`:
   * `/with_position` never persists, so a just-changed mounting plane exists only
   * on the staging position until Select, and the combo's clearance lookup +
   * re-centering must use it.
   * ⚠ x/y are deleted ONLY when null — negatives and auto-positioned values ARE
   * still sent (measured `"x":-3094.104` with `default_pos_x:true`); the server
   * pops them because `default_pos_x` is truthy (`louver_viewset.py:798-801`).
   * This is deliberately NOT `getAccessoryData`'s `positionFields` (which also
   * carries `mounting_type` and drops negatives).
   */
  const positionBody = (): Record<string, unknown> => {
    const p = ctx.stagingPosition()?.toJSON() ?? {};
    const body: Record<string, unknown> = {
      x: p.x,
      y: p.y,
      mounting_plane_id: p.mounting_plane_id,
      default_pos_x: p.default_pos_x,
      default_pos_y: p.default_pos_y,
    };
    if (body.x == null) delete body.x;
    if (body.y == null) delete body.y;
    return body;
  };

  /**
   * `_comboRecalc` :1312-1317 / `_comboAutoSize` :2618-2620 — echo the server's
   * enclosure dimensions back into staging.
   * ★ Q4/R4 lives here: `combo_fit_enclosure_to_faces` is `max()`-only, so this
   * echo can only ever GROW the box. Recess → Surface with auto-size off drops
   * `min_enclosure_width` 63 → 51 while `enclosure_width` STAYS 63 — the box is
   * left 12 in too wide. Parity-only: do NOT add a shrink here.
   */
  const applyEnclosureEcho = (data: Record<string, unknown>): void => {
    const sm = ctx.staging();
    if (!sm) return;
    (['enclosure_width', 'enclosure_height', 'enclosure_depth'] as const).forEach((k) => {
      if (data[k] != null) sm.set({ [k]: data[k] });
    });
  };

  /**
   * Write the server's per-face airways back.
   *
   * ★ THE FILTER IS SUBTLE AND LOAD-BEARING. `_comboRecalc` (:1293-1305) guards
   * with `if (opts.face !== f.face) return;` — so when `opts.face` is NULL the
   * guard matches NOTHING and **no face is echoed at all**. That is deliberate:
   * every trigger other than a direct per-face edit must leave every face's W×H
   * exactly as the user left them (only `edited_face` may be clamped by the
   * server). `_comboAutoSize` (:2631) and `getAccessoryData` (:3763) have NO such
   * guard and apply ALL faces — `onlyFace: false` selects that behaviour.
   */
  const applyFaceEcho = (
    rows: Array<Record<string, unknown>>,
    onlyFace: ComboFace | null | false
  ): void => {
    rows.forEach((f) => {
      const name = String(f.face ?? '') as ComboFace;
      if (!COMBO_FACES.includes(name)) return;
      if (onlyFace !== false && onlyFace !== name) return;
      const s = face(name);
      s.aw_width = f.aw_width == null ? '' : (f.aw_width as number);
      s.aw_height = f.aw_height == null ? '' : (f.aw_height as number);
      // ★ Q2: legacy's appended "missing option" belongs to the aw_dims pass and
      // does not survive the next render, so a size the SERVER just wrote is NOT
      // restored — the select renders blank. Dropping the snapshot here is that.
      s.aw_width_extra = null;
      s.aw_height_extra = null;
      s.active = f.active as boolean;
      // Louver frame depth (KA-5253) — kept per-face for the 3-D canvas (A2.3).
      // ⚠ DEAD-2: `frame_depth` is not a per-face serializer field, so it is
      // always null; stored anyway so this reads 1:1 with legacy (:1300-1301).
      s.depth = f.depth == null ? null : (f.depth as number);
      s.frame_depth = f.frame_depth == null ? null : (f.frame_depth as number);
    });
  };

  /**
   * `_comboRecalc` :1241-1333 — recompute the combo WITHOUT a desired face
   * velocity, so the server returns the recomputed ACTUAL FV + APD against the
   * user's in-progress values and does NOT re-auto-size. Server mode 3
   * (`combo_fit_enclosure_to_faces`, grow-only).
   *
   * `edited_face` is the ONLY field that lets the server grow a dimension (front →
   * `enclosure_width`, left/right → `enclosure_depth`); null for every other
   * trigger, so the enclosure and every other face's `aw_width` are left exactly
   * as they are.
   *
   * 350 ms debounce on its OWN timer (Q11), and it awaits the per-face lookups
   * first so a just-resolved `master_louver_id` lands in this same round trip.
   */
  const recalc = (opts: { face?: ComboFace | null } = {}): void => {
    if (recalcTimer) clearTimeout(recalcTimer);
    recalcTimer = setTimeout(() => {
      void (async () => {
        await awaitPendingLookups();
        const sm = ctx.staging();
        if (!sm) return;
        const model = sm.toJSON();
        const depthVal = ctx.domValue('enclosure_depth');
        // key order = legacy's louverBody :1258-1274, verbatim.
        const louverBody: Record<string, unknown> = {
          section_id: ctx.sectionId,
          is_combo: true,
          airflow_actual: model.airflow_actual,
          louver_params: model.louver_params,
          casing_mount_id: model.casing_mount_id,
          air_direction_id: model.air_direction_id,
          air_stream_id: model.air_stream_id,
          blade_direction_id: model.blade_direction_id,
          enclosure_width: comboNum(model.enclosure_width),
          enclosure_height: comboNum(model.enclosure_height),
          enclosure_depth: depthVal ? comboNum(depthVal) : comboNum(model.enclosure_depth),
          enclosure_material_id: model.enclosure_material_id || null,
          combo_faces: faceBodies(true),
          auto_size: ctx.autoSizeEnabled(),
          edited_face: opts.face ?? null,
        };
        const resp = await ctx.patchWithPosition({
          louver: louverBody,
          position: positionBody(),
        });
        const data = resp?.louver as Record<string, unknown> | undefined;
        if (!data) return;
        // response application order — :1284-1330, verbatim.
        // width_calc/height_calc (NOT enclosure_width/height) are what the canvas
        // preview reads to size the combo box, so they must refresh here too.
        sm.set({
          face_velocity_actual: data.face_velocity_actual,
          apd: data.apd,
          width_calc: data.width_calc,
          height_calc: data.height_calc,
        });
        if (Array.isArray(data.combo_faces)) {
          // ONLY the face the user just edited (and NOTHING when `edited_face` is
          // null — see applyFaceEcho): the server may have clamped it to fit the
          // enclosure. Every other trigger must leave every face's W×H exactly as
          // the user left them.
          // ⚠ DIVERGENCE, unavoidable: legacy's DOM half of this echo
          // (`$row.find('input[name="combo_aw_width"]').val(...)`, :1303-1304)
          // targets `input`, but the template renders a `<select>` — so that write
          // is a silent NO-OP and the face select visibly LAGS the model until some
          // later render. React's selects are driven off the same state that was
          // just updated, so the clamped value becomes visible immediately. Only
          // observable when the server actually clamps `edited_face`, which needs
          // the A1.4 per-face handler.
          applyFaceEcho(data.combo_faces as Array<Record<string, unknown>>, opts.face ?? null);
          if (opts.face) recalcMasterLouverId(opts.face);
        }
        applyEnclosureEcho(data);
        // the footprint may have changed size and the server re-centres it.
        if (resp?.position) ctx.stagingPosition()?.set(resp.position);
        ctx.updateCanvasPreview();
        updateFaceVelocityColor();
        // legacy then re-runs validateNumberField over every number input to
        // reformat the raw server values; React's inputs format declaratively
        // (formatDecimalDisplay), so `render()` is that step.
        ctx.render();
      })();
    }, 350);
  };

  // -----------------------------------------------------------------------
  // A1.3 — the gates
  // -----------------------------------------------------------------------

  /**
   * `_comboNeverSized` :2542-2544. `combo_fit_enclosure_to_faces` can set
   * `enclosure_width` from the front face but NEVER independently sets
   * `enclosure_height` — only `combo_autosize` does — so missing EITHER counts as
   * never sized.
   */
  const neverSized = (): boolean => {
    const sm = ctx.staging();
    return !sm?.get('enclosure_width') || !sm?.get('enclosure_height');
  };

  /**
   * `_comboShouldAutoSize` :2555-2557 — "never sized" OVERRIDES the toggle, and
   * that is load-bearing: with the toggle off and no size yet, only the
   * lightweight recalcs run and they deliberately leave the dimensions "as-is" —
   * there is no "as-is" yet, so the box would never get a size and would be
   * destroyed on the canvas (:2546-2554).
   */
  const shouldAutoSize = (): boolean => !!ctx.autoSizeEnabled() || neverSized();

  /**
   * `_comboReady` :2526-2536 — every input the sides-first sizing needs: mfg +
   * series (frame flange), casing mount + mounting plane (the clearance table),
   * airflow, desired FV and depth. The last two are read off the DOM, as legacy
   * does.
   */
  const ready = (): boolean => {
    const sm = ctx.staging();
    if (!sm) return false;
    const p = params();
    const pos = ctx.stagingPosition()?.toJSON() ?? {};
    return !!(
      p.mfg_id &&
      p.series_id &&
      sm.get('casing_mount_id') &&
      pos.mounting_plane_id &&
      sm.get('airflow_actual') &&
      ctx.domValue('desired_face_velocity_actual') &&
      ctx.domValue('enclosure_depth')
    );
  };

  /**
   * `_comboAutoSize` :2588-2662 — preview the sides-first sizing via
   * `/with_position` (server mode 1) and apply the sized enclosure + per-face
   * airways + combined FV/APD to STAGING ONLY. Nothing here is saved; the combo is
   * persisted only by `_persistCombo` (A1.6).
   *
   * ⚠ Q13: this body deliberately OMITS `enclosure_width`/`enclosure_height` so
   * the server re-derives them from the fitted front airway — while
   * `getAccessoryData`'s combo merge INCLUDES them. Two different bodies hit the
   * same endpoint within ~700 ms on some paths. Both are correct as written.
   * It also omits `edited_face` entirely (mode 1 does not use it).
   *
   * ⚠ It does NOT await the per-face lookups (legacy does not) — only `recalc`
   * and `getAccessoryData` do.
   */
  const autoSize = async (): Promise<void> => {
    const sm = ctx.staging();
    if (!sm) return;
    const model = sm.toJSON();
    const depthVal = ctx.domValue('enclosure_depth');
    const fvVal = ctx.domValue('desired_face_velocity_actual');
    // key order = legacy's louverBody :2599-2610, verbatim.
    const louverBody: Record<string, unknown> = {
      section_id: ctx.sectionId,
      is_combo: true,
      auto_size: ctx.autoSizeEnabled(),
      airflow_actual: model.airflow_actual,
      louver_params: model.louver_params,
      casing_mount_id: model.casing_mount_id,
      air_direction_id: model.air_direction_id,
      air_stream_id: model.air_stream_id,
      blade_direction_id: model.blade_direction_id,
      enclosure_depth: depthVal ? comboNum(depthVal) : null,
      desired_face_velocity: fvVal ? comboNum(fvVal) : null,
      combo_faces: faceBodies(false),
    };
    const resp = await ctx.patchWithPosition({ louver: louverBody, position: positionBody() });
    const data = resp?.louver as Record<string, unknown> | undefined;
    if (!data) return;
    sm.set({
      is_combo: true,
      auto_size: data.auto_size,
      enclosure_width: data.enclosure_width,
      enclosure_height: data.enclosure_height,
      enclosure_depth: data.enclosure_depth,
      face_velocity_actual: data.face_velocity_actual,
      apd: data.apd,
      width_calc: data.width_calc,
      height_calc: data.height_calc,
    });
    if (Array.isArray(data.combo_faces)) {
      // ALL faces (`false`) — unlike _comboRecalc, which echoes only `edited_face`.
      applyFaceEcho(data.combo_faces as Array<Record<string, unknown>>, false);
      // auto-size just established every face's W×H server-side, bypassing the
      // per-face <select> change event — recalc here or the ids stay null.
      recalcAllMasterLouverIds();
    }
    if (resp?.position) ctx.stagingPosition()?.set(resp.position);
    ctx.render();
    updateFaceVelocityColor();
    ctx.updateCanvasPreview();
  };

  /**
   * `_maybeComboAutoSize` :2563-2581 — ★ the promise the ordering fix hangs off.
   * Returns a promise that resolves once this (debounced) attempt SETTLES: after
   * `autoSize()`'s round trip completes, or immediately if it decides not to run.
   * `toggleComboAutoSize` chains `.then(() => fetchAwDims())` onto it so the
   * option lists are never fetched against dimensions that are about to change.
   *
   * Readiness is re-checked INSIDE the debounce, not synchronously: during a burst
   * of field changes (with partial re-renders) the DOM inputs / staging model are
   * transiently incomplete, which used to make this no-op and leave the combo
   * unsized until the user toggled auto-size.
   *
   * ⚠ Faithful quirk: a superseding call `clearTimeout`s the previous timer, so
   * the PREVIOUS promise never settles and any `.then()` chained to it is dropped.
   */
  const maybeAutoSize = (): Promise<void> => {
    if (!ctx.comboMode() || !shouldAutoSize()) return Promise.resolve();
    if (autoTimer) clearTimeout(autoTimer);
    return new Promise<void>((resolve) => {
      autoTimer = setTimeout(() => {
        if (ctx.comboMode() && shouldAutoSize() && ready()) resolve(autoSize());
        else resolve();
      }, 350);
    });
  };

  // -----------------------------------------------------------------------
  // A1.2 — the shared smartlist pick
  // -----------------------------------------------------------------------

  /**
   * `_applyComboSmartlistPick` :1588-1627 — sets the SHARED identity (mfg /
   * series / blade orientation / frame material, common to all three faces) then
   * mirrors `changeParamsField`'s follow-ups. Airway sizes are untouched.
   *
   * ★ MIRRORED DEFECT Q2 (inventory §5.2(i), §8): `await fetchAwDims()` runs
   * BEFORE `getAccessoryData()` + `maybeAutoSize()`, so the lists are bounded by
   * the PRE-RESIZE depth. Measured: at `depth=12` the `&side=true` list is EMPTY,
   * so both side selects render BLANK even though the faces do hold 22 × 62 from
   * the server — the data is right, only the missing `<option>` is not. This is
   * the same defect class the two dev commits fixed for the toggle and casing-
   * mount paths, and it is deliberately left unfixed here: parity-only.
   */
  const applySmartlistPick = async (row: ComboSmartlistRow): Promise<void> => {
    const sm = ctx.staging();
    if (!sm) return;
    const rows = (ctx.cms()?.combo_smartlist as ComboSmartlistRow[] | undefined) ?? [];
    const next = applyComboSmartlistPickParams(params(), row, rows);
    sm.set({ louver_params: JSON.stringify(next) });
    ctx.markChanged();
    ctx.setParamsChangedAutoPick();
    ctx.closeSmartlist();
    // `_syncManufacturerSeriesFilters()` + the `.ka-series-select` teardown /
    // `_initCustomSeriesSelect()` rebuild (:1616-1619) need no explicit call
    // here: the cascade's hide sets are derived in the selector's render body
    // and its auto-pick / invalidate-clear half runs in an effect keyed to the
    // render counter (`syncManufacturerSeriesFilters`), so `render()` below
    // drives both. The series widget is a declarative component — and for a
    // LOUVER legacy ends up with a plain native select anyway, because the
    // `getAccessoryData()` re-render that follows wipes the widget (measured:
    // no `.ka-series-select` in the louver dialog, before or after a pick).
    ctx.render();
    ctx.setImgComponent();
    await fetchAwDims(); // ★ Q2 — before the resize, on purpose.
    void ctx.getAccessoryData();
    void maybeAutoSize();
    ctx.updateCanvasPreview();
  };

  // -----------------------------------------------------------------------
  // A1.4 — the face-row handlers, Reset, and the `.not-valid` pass
  // -----------------------------------------------------------------------

  /**
   * `toggleComboFace` :1832-1872 — the three face checkboxes.
   *
   * ★ THE LAST REMAINING CHECKED FACE CANNOT BE UNCHECKED. Guarded twice in
   * legacy — the template renders it `disabled` (`checkbox_disabled` =
   * `checked && checkedCount <= 1`, already live from wave 1) AND the handler
   * re-checks (:1839-1847, `$cb.prop('checked', true); return;`). Both are kept:
   * React's checkbox is controlled off `ComboFaceState.checked`, so bailing here
   * re-renders it checked, which IS legacy's `prop('checked', true)`.
   * ⚠ `anyOther` tests `checked` TRUTHILY, verbatim (see `comboAnyFaceChecked`).
   *
   * Then the two branches (§5.2(g)):
   *  · auto-size ON, or never sized → a full sides-first resize for the new set of
   *    checked faces (front width, side widths, enclosure width, FV + APD all
   *    recomputed). NO aw_dims refetch on this path — the option lists are bounded
   *    by the enclosure, and a face toggle does not change what the enclosure CAN
   *    be, only what it IS after the resize echoes back.
   *  · auto-size OFF and already sized → leave every other face and the enclosure
   *    alone; recompute the actual FV client-side then confirm FV + APD from the
   *    server (measured: fv 499 → 654 unchecking one side → 948 unchecking both).
   *
   * ⚠ `checked` is the ONLY per-face flag ever sent (`_sync_combo` whitelists it,
   * `louver_viewset.py:135`); `active` is derived server-side, so a face whose
   * checkbox stays checked can still come back inactive — the sides-first
   * auto-size REMOVES the front louver when the sides over-provide the area
   * (models_accessory_louver.py:1952-1956). Nothing here touches `active`.
   */
  const toggleFace = (f: ComboFace, checked: boolean): void => {
    const bag = ctx.faces();
    if (!bag) return;
    const state = face(f);
    if (!checked) {
      const anyOther = COMBO_FACES.some((o) => o !== f && !!bag[o]?.checked);
      if (!anyOther) {
        ctx.render(); // = `$cb.prop('checked', true)` — the box stays checked.
        return;
      }
    }
    state.checked = checked;
    if (!checked) {
      // Unchecked → this face's validation state and catalog pick are stale.
      state.master_louver_id = null;
      state.pendingLouverUrl = null;
      setFaceInvalid(f, false);
    }
    // legacy toggles a plain client flag (the face bag is not a model field) so it
    // never arms anything; React's /with_position preview is userArmed-gated, so
    // the toggle must arm or the resize below could not fire at all. A face toggle
    // IS a real user edit whose result Select persists — arming is correct.
    ctx.markChanged();
    ctx.render();
    if (shouldAutoSize()) {
      void maybeAutoSize();
    } else {
      updateFaceVelocity();
      recalc();
    }
    // Re-checking a face may make it eligible for a lookup again; unchecking
    // already cleared it above.
    if (checked) recalcAllMasterLouverIds();
  };

  /**
   * `onComboFaceFieldChange` :1131-1159 — one face's W or H `<select>`.
   *
   * The value is taken as entered and NOT capped: the front width drives the
   * enclosure WIDTH and each side width drives the DEPTH, and it is the server
   * that grows the enclosure to fit (`edited_face` is the only field that lets it
   * grow a dimension). So this sends `edited_face: <face>` and reflects whatever
   * comes back.
   *
   * ⚠ DIVERGENCE, deliberate and documented (see `recalc`'s response block):
   * legacy's per-face echo writes the clamped value with
   * `$row.find('input[name="combo_aw_width"]').val(...)` (:1303-1304) — but the
   * template renders a `<select>`, so that write is a SILENT NO-OP and legacy's
   * face select visibly LAGS the model until some later `render()` rebuilds it.
   * React's selects are declarative and re-render off the same state the echo just
   * updated, so a server clamp of `edited_face` becomes visible IMMEDIATELY. There
   * is no way to reproduce the lag without deliberately holding a second, stale
   * copy of the value purely to render from. This is observable ONLY through this
   * handler (nothing else sends `edited_face`), and only when the server actually
   * clamps — so it is called out rather than emulated.
   */
  const onFaceField = (
    f: ComboFace,
    name: 'combo_aw_width' | 'combo_aw_height',
    raw: string
  ): void => {
    if (!ctx.faces()) return;
    const state = face(f);
    const val = raw ? parseFloat(String(raw).replace(/,/g, '')) : null;
    if (name === 'combo_aw_width') state.aw_width = val;
    else state.aw_height = val;
    // The airway just changed → the previously picked louver for this face is
    // stale; clear the id + flag now, the fresh lookup below settles both.
    state.master_louver_id = null;
    setFaceInvalid(f, false);
    ctx.markChanged();
    // Manual dimension change: recompute the actual FV now (client side, for
    // immediacy) then confirm FV + APD from the server. The dimensions themselves
    // are left as the user entered them (no re-auto-size).
    updateFaceVelocity();
    recalc({ face: f });
    recalcAllMasterLouverIds();
    // legacy then calls `_updateSelectButtonState()` because `anyFaceSized`
    // (read by `_isSelectValid`) just changed. React pushes the gate from an
    // effect that runs after EVERY render, and `updateFaceVelocity()` above
    // already rendered — so the button refreshes without an explicit call.
  };

  /**
   * `resetComboFace` :1512-1541 — the *Reset* span next to "Louvers x / y".
   *
   * Resets the whole combo back to an UNSIZED state: the shared louver identity
   * (mfg / series / blade orientation / frame material + the four cached `*_name`
   * twins) AND every face's airway pair AND `enclosure_width`/`enclosure_height`.
   * The face `checked` states and the enclosure DEPTH survive.
   *
   * ★ TWO MEASURED DEFECTS MIRRORED, NOT FIXED (inventory Q5 + Q6, the prompt's
   * "Q5"):
   *  1. **APD is left STALE.** Nothing here clears `apd`, and
   *     `updateFaceVelocity()` only writes `face_velocity_actual` (which does go
   *     null, since every face area is now empty). Measured: `apd = 0.02` still
   *     displayed after Reset cleared FV and the identity. Do NOT add
   *     `apd: null` — that would diverge.
   *  2. **The NEXT `aw_dims` fetch 500s.** `enclosure_width`/`enclosure_height`
   *     are now null and `buildComboAwDimsUrl` concatenates them, so the query
   *     carries the literal `&width=null&height=null` and
   *     `louvers_viewset.py:692-715`'s bare `float()` raises → 500 (RISK R1,
   *     reproduced 6× = 2 calls × 3 attempts). The `fetchAwDims()` below is NOT
   *     it: the identity was just cleared, so it takes the early-return branch and
   *     issues no HTTP at all — it only empties the option lists (which is the
   *     measured "the size dropdowns are empty after Reset"). The 500 lands on the
   *     NEXT fetch, i.e. as soon as the identity is re-picked, and it aborts that
   *     chain — hence "the combo cannot be re-sized until the depth is edited"
   *     (editing the depth runs the resize, which re-establishes W/H). Neither the
   *     rejection nor the missing params may be defended against.
   */
  const reset = (): void => {
    const sm = ctx.staging();
    if (!sm) return;
    // legacy: NON-silent (`staging_model.set('louver_params', …)`).
    sm.set({ louver_params: JSON.stringify(resetComboIdentityParams(params())) });
    resetComboFaces(ctx.faces());
    // legacy: SILENT — `{ enclosure_width: null, enclosure_height: null }`.
    sm.set({ enclosure_width: null, enclosure_height: null });
    ctx.markChanged();
    updateFaceVelocity();
    // Shared identity cleared → the option lists must go empty too (this also
    // recalcs every face's master_louver_id, now null again).
    void fetchAwDims();
    ctx.render();
    ctx.updateCanvasPreview();
  };

  /**
   * `_applyComboValidation` :1038-1087 — the `.not-valid` display pass.
   *
   * Legacy toggles the class straight on the DOM and reads the enclosure values
   * back OFF THE INPUTS (not the staging model), so this is ported as the same
   * out-of-band DOM pass rather than as render state — same reasoning as
   * `updateFaceVelocityColor` above, and it keeps the values it judges identical
   * to the ones the user sees (the inputs carry `formatDecimalDisplay` text).
   * `#component_rating_mode` is React's dialog host, i.e. `self.$el`.
   *
   * Three checks:
   *  · depth outside `[max(12, cms.combo_min_depth), cms.combo_max_depth]`, and
   *    an EMPTY depth counts as bad (`isNaN`);
   *  · width outside the CMS `[min, max]`;
   *  · height outside the CMS `[min, max]` OR above the section airway height.
   * Plus the face-row flag when nothing is checked at all (unreachable through the
   * UI thanks to the last-face guard, but Reset does not re-check faces either, so
   * legacy computes it and so do we).
   *
   * ⚠ RISK R7: every `combo_*` CMS limit is NULL on dev3, so only the hard 12"
   * depth floor and the airway-height cap can fire there — the CMS branches are
   * implemented from the code and cannot be exercised on that DB.
   *
   * Legacy ends with `_updateSelectButtonState()`; React pushes `#select` from an
   * effect that runs after every render, and every caller renders, so there is no
   * separate call. `toggleClass` (both directions) means a later valid edit clears
   * the flag — unlike legacy, whose `render()` also wipes it, React's `className`
   * prop is unchanged so an imperatively added class survives until the next pass.
   */
  const applyValidation = (): void => {
    if (!ctx.comboMode()) return;
    const cms = ctx.cms() ?? {};
    const root = document.querySelector('#component_rating_mode');
    if (!root) return;
    const num = (v: unknown): number => parseFloat(String(v ?? '').replace(/,/g, ''));
    const input = (name: string) => root.querySelector<HTMLInputElement>(`input[name="${name}"]`);
    const flag = (el: Element | null | undefined, bad: boolean): void => {
      el?.classList.toggle('not-valid', bad);
    };

    // depth — the hard 12" floor OR'd over the CMS min.
    const min = Math.max(12, Number(cms.combo_min_depth) || 0);
    const max = cms.combo_max_depth ? Number(cms.combo_max_depth) : null;
    const depthEl = input('enclosure_depth');
    const depthRaw = depthEl?.value ?? '';
    const depth = depthRaw ? num(depthRaw) : NaN;
    flag(depthEl, Number.isNaN(depth) || depth < min || (!!max && depth > max));

    // width — within the CMS [min, max].
    const wEl = input('enclosure_width');
    if (wEl) {
      const wmin = cms.combo_louver_enclosure_width_min;
      const wmax = cms.combo_louver_enclosure_width_max;
      const wv = num(wEl.value);
      flag(
        wEl,
        !Number.isNaN(wv) &&
          ((wmin != null && wv < Number(wmin)) || (wmax != null && wv > Number(wmax)))
      );
    }

    // height — within the CMS [min, max] AND <= the section airway height.
    const hEl = input('enclosure_height');
    if (hEl) {
      const hmin = cms.combo_louver_enclosure_height_min;
      const hmax = cms.combo_louver_enclosure_height_max;
      const awH = parseFloat(String(ctx.sectionAwHeight() ?? ''));
      const hv = num(hEl.value);
      flag(
        hEl,
        !Number.isNaN(hv) &&
          ((hmin != null && hv < Number(hmin)) ||
            (hmax != null && hv > Number(hmax)) ||
            (!!awH && hv > awH))
      );
    }

    // face rows — flagged only when NOTHING is checked.
    const anyFace = comboAnyFaceChecked(ctx.faces());
    root.querySelectorAll('.combo-face-row').forEach((row) => {
      row.classList.toggle('not-valid', !anyFace);
    });
  };

  // -----------------------------------------------------------------------
  // A1.6 — PERSIST
  // -----------------------------------------------------------------------

  /**
   * `_persistCombo` :2667-2714 — THE combo write. `PATCH /ahu/louvers/<id>` with
   * the current enclosure + per-face selections and **deliberately no
   * `desired_face_velocity`**, so `louver_viewset.py:137-149` dispatches to
   * `combo_fit_enclosure_to_faces` (the grow-only fit) and the user's picks are
   * SAVED instead of re-auto-sized. `edited_face` is absent too.
   *
   * Ordering (§4 W3) is the CALLER's: `PATCH /ahu/positions/<id>` goes FIRST and
   * is combo-only (`selectAccessory` :2834 — a combo immediately PATCHes its
   * position instead of relying on the position model's own debounced save).
   * `/with_position` NEVER persists; it is the preview. Those are different
   * endpoints and only this one saves.
   *
   * ★ RACE GUARD: `awaitPendingLookups()` first, so a `master_louver_id` resolved
   * a moment ago lands in THIS body instead of racing it — otherwise the face
   * would persist with `master_louver_id: null`.
   *
   * Key order is legacy's verbatim (:2679-2694) so the wire body diffs
   * byte-identically. `enclosure_depth` is read off the DOM input first, exactly
   * as legacy does.
   *
   * ⚠ RISK R9 (report-only): `casing_mount_id` is sent as-is. `null` would 500
   * (`louver_viewset.py:242-249` does `int(...)` inside an `in request.data`
   * check). Legacy does not guard either, and the server always defaults it to 1
   * at create — so no client-side guard is added.
   *
   * The response IS trustworthy: `partial_update` re-serializes after
   * `recenter_combo_enclosure` (:480-482), unlike POST/PUT which return a
   * pre-`_sync_combo` payload (R11).
   */
  const persist = async (): Promise<Record<string, unknown> | null> => {
    await awaitPendingLookups();
    const sm = ctx.staging();
    if (!sm) return null;
    const model = sm.toJSON();
    const depthVal = ctx.domValue('enclosure_depth');
    const body: Record<string, unknown> = {
      section_id: ctx.sectionId,
      is_combo: true,
      auto_size: ctx.autoSizeEnabled(),
      airflow_actual: model.airflow_actual,
      louver_params: model.louver_params,
      casing_mount_id: model.casing_mount_id,
      air_direction_id: model.air_direction_id,
      air_stream_id: model.air_stream_id,
      blade_direction_id: model.blade_direction_id,
      enclosure_width: comboNum(model.enclosure_width),
      enclosure_height: comboNum(model.enclosure_height),
      enclosure_depth: depthVal ? comboNum(depthVal) : comboNum(model.enclosure_depth),
      enclosure_material_id: model.enclosure_material_id || null,
      combo_faces: faceBodies(true),
    };
    return ctx.patchLouver(body);
  };

  return {
    fetchAwDims,
    recalc,
    maybeAutoSize,
    autoSize,
    neverSized,
    shouldAutoSize,
    ready,
    recalcMasterLouverId,
    recalcAllMasterLouverIds,
    applyServerFaces: (rows) => applyFaceEcho(rows, false),
    awaitPendingLookups,
    applySmartlistPick,
    updateFaceVelocity,
    updateFaceVelocityColor,
    faceBodies,
    positionBody,
    toggleFace,
    onFaceField,
    reset,
    applyValidation,
    persist,
    dispose: () => {
      if (recalcTimer) clearTimeout(recalcTimer);
      if (autoTimer) clearTimeout(autoTimer);
    },
  };
}
